import ExcelJS from 'exceljs';
import { resolveProductUnit } from './unitSelectUtils';

const COL = {
  sku: 'SKU o ID producto',
  qty: 'Cantidad',
  unit: 'Unidad (código)',
  price: 'Precio unitario',
  project: 'Proyecto (código)',
  condition: 'Condición',
  event: 'Evento kardex',
};

const CONDITIONS_ES = {
  bueno: 'good',
  good: 'good',
  dañado: 'damaged',
  danado: 'damaged',
  damaged: 'damaged',
  defectuoso: 'defective',
  defective: 'defective',
  reutilizable: 'reusable',
  reusable: 'reusable',
  'no reutilizable': 'non_reusable',
  'no reutilizables': 'non_reusable',
  non_reusable: 'non_reusable',
  'no_reusable': 'non_reusable',
};

const ENTRY_EVENTS = {
  compra: 'compra',
  devolucion: 'devolucion',
  devolución: 'devolucion',
  transferencia_gratuita: 'transferencia_gratuita',
  'transferencia gratuita': 'transferencia_gratuita',
  descuento_obtenido: 'descuento_obtenido',
  'descuento obtenido': 'descuento_obtenido',
  descuento_con_mercaderia: 'descuento_con_mercaderia',
  'descuento con mercaderia': 'descuento_con_mercaderia',
  'descuento con mercadería': 'descuento_con_mercaderia',
};

const EXIT_EVENTS = {
  venta: 'venta',
  descuento_concedido: 'descuento_concedido',
  'descuento concedido': 'descuento_concedido',
  consumo: 'consumo',
  transferencia_gratuita: 'transferencia_gratuita',
  'descuento con mercaderia': 'descuento_con_mercaderia',
  'descuento con mercadería': 'descuento_con_mercaderia',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFCFFAFE' },
};

const MAX_IMPORT_ROWS = 2000;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

/** En guía de salida el sistema fija costo y precio desde el almacén; el Excel no debe traer importes. */
const FORBIDDEN_EXIT_PRICE_HEADERS = [
  COL.price,
  'Costo unitario',
  'Costo',
  'Precio',
  'P. unitario',
  'P. unit',
  'PU',
  'Subtotal',
  'Sub total',
  'Importe',
  'Total línea',
];

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function cellText(cell) {
  if (cell == null) return '';
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object' && v.text != null) return String(v.text).trim();
  return String(v).trim();
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function getGuideVariantKey({ isInitial, isExit, isTransfer, isReception }) {
  if (isReception) return 'recepcion';
  if (isInitial) return 'inicial';
  if (isTransfer) return 'traspaso';
  if (isExit) return 'salida';
  return 'entrada';
}

/**
 * Texto de ayuda por tipo de guía (solo se usa al generar la hoja "Instrucciones" del Excel).
 */
function getGuideItemsImportLegend({ isInitial, isExit, isTransfer, isReception } = {}) {
  const v = getGuideVariantKey({ isInitial, isExit, isTransfer, isReception });
  const baseCols = [
    `• ${COL.sku} (obligatorio): código SKU del producto, o solo el ID numérico del producto.`,
    `• ${COL.qty} (obligatorio): número mayor que 0 (puede usar coma o punto decimal).`,
    `• ${COL.unit} (opcional): código de unidad (ej. kg, UNI). Si lo omite, se usa la unidad del producto.`,
  ];
  if (v === 'salida') {
    baseCols.push(
      '• No use columnas de precio, costo unitario, subtotal ni importe: en salida el valor y el subtotal de cada fila los calcula el sistema según el stock y costo del almacén (kardex) al confirmar o procesar la guía. La hoja «Items» solo trae: SKU, cantidad, unidad, proyecto, condición, evento de kardex.',
    );
  } else {
    baseCols.push(`• ${COL.price} (opcional): costo unitario para el valor de la mercadería en esta guía.`);
  }
  baseCols.push(
    `• ${COL.project} (opcional): código del proyecto (ej. el código que ve en el desplegable de Proyecto).`,
    `• ${COL.condition} (opcional): bueno | dañado | defectuoso | reutilizable | no reutilizable (o en inglés: good, damaged, …). Por defecto: bueno.`,
  );

  const eventNote =
    v === 'entrada'
      ? `• ${COL.event} (opcional): un evento de compra u otros por fila. Valores: compra, devolucion, devolución, transferencia_gratuita, transferencia gratuita, descuento_obtenido, descuento obtenido, descuento_con_mercaderia, descuento con mercaderia, descuento con mercadería. Tras importar, complete referencias a guía si aplica.`
      : v === 'salida'
        ? `• ${COL.event} (opcional): venta, descuento_concedido, descuento concedido, consumo, transferencia_gratuita, descuento con mercaderia, descuento con mercadería. (Bitácora en maquinaria: en el formulario.)`
      : v === 'inicial'
        ? `• ${COL.event}: kardex fijo «saldo_inicial». Deje la celda vacía o use saldo_inicial (como en la fila de ejemplo). Otros textos se ignoran con aviso.`
        : v === 'traspaso'
          ? `• ${COL.event}: kardex fijo «traspaso». Deje vacío o use traspaso. Otros textos se ignoran con aviso.`
          : v === 'recepcion'
            ? `• ${COL.event}: kardex fijo «recepcion». Deje vacío o use recepcion. Otros textos se ignoran con aviso.`
            : `• ${COL.event}: use el valor fijo de su tipo de guía.`;

  const tipo =
    v === 'inicial'
      ? 'Guía de saldo inicial: todos los ítems registran el evento «Saldo inicial» en kárdex.'
      : v === 'traspaso'
        ? 'Guía de traspaso: evento fijo «Traspaso».'
        : v === 'recepcion'
          ? 'Recepción desde traspaso: evento fijo «Recepción»; suelen emplear ítems desde la guía de traspaso, pero puede importar líneas adicionales si aplica en su flujo.'
          : v === 'salida'
            ? 'Guía de salida: requiere almacén; costos y stock vienen de la precarga del almacén.'
            : 'Guía de entrada: puede indicar evento de compra (u otros) por fila si la columna está en la plantilla.';

  return { variantKey: v, title: 'Cómo completar la plantilla', baseCols, eventNote, tipo };
}

function getEventReferenceBlock(variantKey) {
  if (variantKey === 'entrada') {
    return [
      'EVENTOS DE KARDEX (columna en hoja «Items»)',
      'Códigos / textos aceptados por fila: compra | devolucion | devolución | transferencia_gratuita | transferencia gratuita | descuento_obtenido | descuento obtenido | descuento_con_mercaderia | descuento con mercaderia | descuento con mercadería',
      'Si deja la celda de evento vacía, se asume: compra.',
    ];
  }
  if (variantKey === 'salida') {
    return [
      'SIN PRECIO NI COSTO EN ESTA PLANTILLA',
      'No añada columnas de precio unitario, costo, subtotal ni importe. El Excel de salida solo indica qué y cuánto sale, el evento de kardex, etc. Los importes y costos de línea son siempre del sistema (stock del almacén).',
      '',
      'EVENTOS DE KARDEX (columna en hoja «Items»)',
      'Códigos / textos aceptados por fila: venta | descuento_concedido | descuento concedido | consumo | transferencia_gratuita | descuento con mercaderia | descuento con mercadería',
      'Si deja la celda de evento vacía, se asume: venta.',
    ];
  }
  if (variantKey === 'inicial') {
    return [
      'EVENTO DE KARDEX (fijo en el sistema: saldo_inicial)',
      'La columna «Evento kardex» es opcional: vacío o el texto saldo_inicial. No use otros códigos de evento; si lo hace, se mostrará un aviso y se aplicará saldo_inicial de todas formas.',
    ];
  }
  if (variantKey === 'traspaso') {
    return [
      'EVENTO DE KARDEX (fijo: traspaso)',
      'Columna opcional: vacía o traspaso. Cualquier otro valor se ignora con aviso; el asiento de kardex es siempre traspaso.',
    ];
  }
  if (variantKey === 'recepcion') {
    return [
      'EVENTO DE KARDEX (fijo: recepcion)',
      'Columna opcional: vacía o recepcion. Cualquier otro valor se ignora con aviso; el asiento de kardex es siempre recepcion.',
    ];
  }
  return [];
}

function buildInstructionRows(legend) {
  const eventRef = getEventReferenceBlock(legend.variantKey);
  return [
    'LEYENDA Y USO DE LA PLANTILLA',
    legend.tipo,
    '',
    'COLUMNAS (no cambie el texto de la primera fila de la hoja «Items»)',
    legend.eventNote,
    ...legend.baseCols,
    '',
    ...eventRef,
    '',
    'VALIDACIÓN AL IMPORTAR EN EL SISTEMA',
    '• Si unidad, proyecto, condición o evento de kardex (cuando aplica) no son reconocidos, la fila se rechaza; no se importa parcialmente.',
    '• El producto debe existir en el catálogo; si en el formulario eligió almacén, el producto debe figurar en ese almacén (mismos criterios que al agregar manualmente).',
    ...(legend.variantKey === 'salida'
      ? [
          '• Cantidad obligatoria, mayor que 0.',
          '• Guía de salida: el archivo no debe contener columna de precio, costo unitario, subtotal ni importe; de lo contrario el import se rechaza. Use la plantilla descargada (solo columnas de producto, cantidades, evento, etc.).',
        ]
      : [
          '• Cantidad obligatoria, mayor que 0. Precio unitario, cuando aplica a este tipo de guía, no negativo y numérico.',
        ]),
    '• Unidades, proyectos y textos: respete mayúsculas o use los códigos exactos (ej. código de unidad) según se indique arriba.',
    '• Si tiene activas las conversiones por producto, en «Unidad» puede usar códigos de tasas personalizadas (ej. BOLSA) configuradas en cada producto, además del catálogo general.',
    '',
    'EVENTOS ESPECIALES',
    '• Devolución: si aplica, complete después en pantalla la referencia a guía u orden; la plantilla solo define el producto, cantidad y evento de línea según su tipo de guía.',
    '• Productos con registro de maquinaria (guías de salida): use la acción de bitácora en cada fila tras importar.',
    '',
    'Use solo el archivo .xlsx descargado. La hoja con datos se llama «Items»; si se copia a otro libro, la primera hoja con las columnas requeridas también sirve. No use Excel .xls antiguo.',
  ];
}

/**
 * Hoja 1 = datos; hoja 2 = instrucciones (leyenda completa solo en Excel).
 */
export async function downloadGuideItemsTemplate({ isInitial, isExit, isTransfer, isReception } = {}) {
  const legend = getGuideItemsImportLegend({ isInitial, isExit, isTransfer, isReception });
  const v = getGuideVariantKey({ isInitial, isExit, isTransfer, isReception });
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Items', { views: [{ state: 'frozen', ySplit: 1 }] });

  let headers;
  if (v === 'salida') {
    headers = [COL.sku, COL.qty, COL.unit, COL.project, COL.condition, COL.event];
  } else if (v === 'entrada') {
    headers = [COL.sku, COL.qty, COL.unit, COL.price, COL.project, COL.condition, COL.event];
  } else {
    headers = [COL.sku, COL.qty, COL.unit, COL.price, COL.project, COL.condition, COL.event];
  }

  const hRow = ws.addRow(headers);
  hRow.font = { bold: true };
  headers.forEach((_, i) => {
    hRow.getCell(i + 1).fill = HEADER_FILL;
  });

  if (v === 'salida') {
    ws.addRow(['EJ-SKU-001', 1, 'kg', 'PROJ-1', 'bueno', 'venta']);
  } else if (v === 'entrada') {
    ws.addRow(['EJ-SKU-001', 1, 'kg', 0, 'PROJ-1', 'bueno', 'compra']);
  } else if (v === 'inicial') {
    ws.addRow(['EJ-SKU-001', 1, 'kg', 0, 'PROJ-1', 'bueno', 'saldo_inicial']);
  } else if (v === 'traspaso') {
    ws.addRow(['EJ-SKU-001', 1, 'kg', 0, 'PROJ-1', 'bueno', 'traspaso']);
  } else {
    ws.addRow(['EJ-SKU-001', 1, 'kg', 0, 'PROJ-1', 'bueno', 'recepcion']);
  }

  ws.columns = headers.map((h) => ({ width: Math.min(28, Math.max(14, h.length + 2)) }));

  const inst = wb.addWorksheet('Instrucciones');
  const instLines = buildInstructionRows(legend);
  instLines
    .filter((line) => line != null)
    .forEach((line, i) => {
    const c = inst.getCell(1 + i, 1);
    c.value = line;
    const bold =
      line === 'LEYENDA Y USO DE LA PLANTILLA' ||
      line === 'COLUMNAS (no cambie el texto de la primera fila de la hoja «Items»)' ||
      line === 'SIN PRECIO NI COSTO EN ESTA PLANTILLA' ||
      (typeof line === 'string' &&
        (line.startsWith('EVENTOS DE KARDEX') || line.startsWith('EVENTO DE KARDEX'))) ||
      line === 'VALIDACIÓN AL IMPORTAR EN EL SISTEMA' ||
      line === 'EVENTOS ESPECIALES';
    if (bold) c.font = { bold: true, size: 12 };
  });
  inst.getColumn(1).width = 100;

  const buf = await wb.xlsx.writeBuffer();
  const name = `plantilla-items-guia-${legend.variantKey || 'default'}.xlsx`;
  downloadBuffer(buf, name);
}

function findProduct(products, raw) {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) {
    const id = parseInt(t, 10);
    const byId = products.find((p) => p.id === id);
    if (byId) return byId;
  }
  const low = t.toLowerCase();
  return (
    products.find(
      (p) =>
        (p.sku && String(p.sku).toLowerCase() === low) ||
        (p.code && String(p.code).toLowerCase() === low),
    ) || null
  );
}

/**
 * @returns {string | null} valor interno o null si el texto no es vacío y no se reconoce
 */
function tryConditionText(raw) {
  if (raw == null || String(raw).trim() === '') return 'good';
  const k = String(raw).trim().toLowerCase();
  const spaced = k.replace(/\s+/g, ' ');
  const mapped = CONDITIONS_ES[k] || CONDITIONS_ES[spaced];
  if (mapped) return mapped;
  if (['good', 'damaged', 'defective', 'reusable', 'non_reusable'].includes(k)) return k;
  return null;
}

/**
 * @returns {string | null} código o null si hay texto y no se reconoce
 */
function tryEntryKardex(raw) {
  if (raw == null || !String(raw).trim()) return 'compra';
  const k = String(raw).trim().toLowerCase();
  return (
    ENTRY_EVENTS[k] || ENTRY_EVENTS[k.replace(/\s+/g, ' ')] || ENTRY_EVENTS[k.replace(/\s+/g, '_')] || null
  );
}

/**
 * @returns {string | null}
 */
function tryExitKardex(raw) {
  if (raw == null || !String(raw).trim()) return 'venta';
  const k = String(raw).trim().toLowerCase();
  return (
    EXIT_EVENTS[k] || EXIT_EVENTS[k.replace(/\s+/g, ' ')] || EXIT_EVENTS[k.replace(/\s+/g, '_')] || null
  );
}

/** Texto de celda vacío o igual al evento fijo del tipo de guía → no se avisa. */
function cellMatchesFixedKardexGuide(isInitial, isTransfer, isReception, evTrim) {
  if (!evTrim) return true;
  const t = String(evTrim).trim().toLowerCase().replace(/\s+/g, ' ');
  if (isInitial) {
    return t === 'saldo_inicial' || t === 'saldo inicial' || t === 'inicial';
  }
  if (isTransfer) return t === 'traspaso';
  if (isReception) return t === 'recepcion' || t === 'recepción';
  return true;
}

function findHeaderRow(sheet) {
  const need = [COL.sku, COL.qty];
  const max = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= max; r++) {
    const row = sheet.getRow(r);
    const m = headerMapFromRow(row);
    if (m[need[0]] && m[need[1]]) {
      return { rowIndex: r, colMap: m };
    }
  }
  return null;
}

function findForbiddenPriceHeaderForExit(colMap) {
  for (const header of Object.keys(colMap)) {
    const h = String(header).trim().toLowerCase();
    for (const f of FORBIDDEN_EXIT_PRICE_HEADERS) {
      if (h === String(f).trim().toLowerCase()) {
        return header;
      }
    }
  }
  return null;
}

/**
 * @returns {Promise<{ items: object[], errors: string[], warnings: string[] }>}
 * Cada `item` es un objeto listo para `formData.items` (mismo shape que `handleAddProduct`).
 */
export async function parseGuideItemsExcel(file, ctx) {
  const {
    products,
    units,
    projects,
    isInitial,
    isExit,
    isTransfer,
    isReception,
    productConversionsEnabled = false,
    resolveProductIdentifiers = null,
  } = ctx;
  const errors = [];
  const warnings = [];
  const items = [];
  if (file && file.size > MAX_FILE_SIZE_BYTES) {
    errors.push('El archivo supera el máximo de 5 MB.');
    return { items, errors, warnings };
  }
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheet = wb.getWorksheet('Items') || wb.worksheets[0];
  if (!sheet) {
    errors.push('No se encontró ninguna hoja en el archivo.');
    return { items, errors, warnings };
  }
  const found = findHeaderRow(sheet);
  if (!found) {
    errors.push(
      `No se encontró la fila de encabezados. Debe incluir las columnas «${COL.sku}» y «${COL.qty}» (nombres exactos).`,
    );
    return { items, errors, warnings };
  }
  const { colMap, rowIndex: headerRow } = found;
  const cSku = colMap[COL.sku];
  const cQty = colMap[COL.qty];
  const cUnit = colMap[COL.unit];
  const cPrice = colMap[COL.price];
  const cProj = colMap[COL.project];
  const cCond = colMap[COL.condition];
  const cEvent = colMap[COL.event];

  let productList = [...(products || [])];
  if (typeof resolveProductIdentifiers === 'function') {
    const identifiers = [];
    const lastScan = sheet.rowCount;
    for (let r = headerRow + 1; r <= lastScan; r++) {
      const row = sheet.getRow(r);
      const skuVal = cSku ? cellText(row.getCell(cSku)) : '';
      const qtyVal = cQty ? cellText(row.getCell(cQty)) : '';
      if (!String(skuVal).trim() && !String(qtyVal).trim()) continue;
      identifiers.push(String(skuVal).trim());
    }
    const uniqueIdentifiers = [...new Set(identifiers.filter(Boolean))];
    if (uniqueIdentifiers.length > 0) {
      const resolved = await resolveProductIdentifiers(uniqueIdentifiers);
      const byId = new Map(productList.map((product) => [product.id, product]));
      (resolved || []).forEach((product) => {
        if (product?.id != null) byId.set(product.id, product);
      });
      productList = [...byId.values()];
    }
  }

  if (isExit) {
    const forbiddenH = findForbiddenPriceHeaderForExit(colMap);
    if (forbiddenH) {
      errors.push(
        `La guía de salida no admite la columna «${forbiddenH}» (ni otras de precio, costo o importe por fila). El costo y el precio unitario los calcula el sistema con el almacén. Use «Descargar plantilla» de salida, sin añadir columnas de importes, o quite esa columna del archivo.`,
      );
      return { items, errors, warnings };
    }
  }

  const last = sheet.rowCount;
  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const skuVal = cSku ? cellText(row.getCell(cSku)) : '';
    const qtyVal = cQty ? cellText(row.getCell(cQty)) : '';
    if (!String(skuVal).trim() && !String(qtyVal).trim()) continue;

    if (items.length >= MAX_IMPORT_ROWS) {
      errors.push(
        `Solo se importan como máximo ${MAX_IMPORT_ROWS} filas. Divida el archivo o elimine filas al final de la hoja.`,
      );
      break;
    }

    if (!String(skuVal).trim()) {
      errors.push(`Fila ${r}: falta ${COL.sku}.`);
      continue;
    }
    const product = findProduct(productList, skuVal);
    if (!product) {
      errors.push(`Fila ${r}: no se encontró producto con SKU/ID «${skuVal}».`);
      continue;
    }
    const qtyNum = parseFloat(String(qtyVal).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      errors.push(`Fila ${r}: ${COL.qty} debe ser un número mayor que 0.`);
      continue;
    }

    let uom = product.unit_of_measure || '';
    let unitFormula = '';
    let unitIsCustom = false;
    if (cUnit) {
      const uRaw = String(cellText(row.getCell(cUnit)) || '').trim();
      if (uRaw) {
        const resolved = resolveProductUnit({
          units,
          product,
          rawUnit: uRaw,
          productConversionsEnabled,
        });
        if (!resolved.ok) {
          errors.push(`Fila ${r}: ${resolved.error}`);
          continue;
        }
        uom = resolved.code;
        unitFormula = resolved.formula || '';
        unitIsCustom = resolved.isCustom;
      }
    }

    let projectId = '';
    if (cProj) {
      const pRaw = String(cellText(row.getCell(cProj)) || '')
        .trim();
      if (pRaw) {
        const pr = projects.find(
          (p) => String(p.code || '')
            .toLowerCase() === pRaw.toLowerCase(),
        );
        if (pr) projectId = String(pr.id);
        else {
          errors.push(
            `Fila ${r}: proyecto «${pRaw}» no encontrado. Deje la celda vacía o use el código del proyecto (véase hoja Instrucciones).`,
          );
          continue;
        }
      }
    }

    const condText = cCond ? cellText(row.getCell(cCond)) : '';
    const cond = tryConditionText(condText);
    if (cond == null) {
      errors.push(
        `Fila ${r}: condición no reconocida. Valores: bueno, dañado, defectuoso, reutilizable, no reutilizable (o en inglés: good, damaged, …).`,
      );
      continue;
    }

    const ev = cEvent ? cellText(row.getCell(cEvent)) : '';
    const evTrim = String(ev || '').trim();
    let kardex;
    if (isInitial) {
      if (evTrim && !cellMatchesFixedKardexGuide(true, false, false, evTrim)) {
        warnings.push(
          `Fila ${r}: el evento «${evTrim}» no aplica a guía inicial; se usará saldo_inicial en kardex.`,
        );
      }
      kardex = 'saldo_inicial';
    } else if (isTransfer) {
      if (evTrim && !cellMatchesFixedKardexGuide(false, true, false, evTrim)) {
        warnings.push(
          `Fila ${r}: el evento «${evTrim}» no aplica; en traspaso el kardex es fijo (traspaso).`,
        );
      }
      kardex = 'traspaso';
    } else if (isReception) {
      if (evTrim && !cellMatchesFixedKardexGuide(false, false, true, evTrim)) {
        warnings.push(
          `Fila ${r}: el evento «${evTrim}» no aplica; en recepción el kardex es fijo (recepcion).`,
        );
      }
      kardex = 'recepcion';
    } else if (isExit) {
      kardex = tryExitKardex(ev);
      if (kardex == null) {
        errors.push(
          `Fila ${r}: evento de kardex no reconocido para salida. Ejemplos: venta, descuento_concedido, consumo, transferencia_gratuita, descuento con mercadería. Consulte la hoja Instrucciones de la plantilla.`,
        );
        continue;
      }
    } else {
      kardex = tryEntryKardex(ev);
      if (kardex == null) {
        errors.push(
          `Fila ${r}: evento de kardex no reconocido para entrada. Use compra, devolucion, transferencia_gratuita, descuento obtenido, descuento con mercaderia, u otros indicados en la hoja Instrucciones del Excel.`,
        );
        continue;
      }
    }

    if (kardex === 'devolucion') {
      warnings.push(
        `Fila ${r}: evento «devolucion» requiere vincular la guía u orden de referencia en el formulario tras importar.`,
      );
    }

    let unitPrice = 0;
    if (!isExit && cPrice) {
      const pRaw = cellText(row.getCell(cPrice));
      if (pRaw && pRaw !== '— (no aplic)' && pRaw !== '—') {
        const pu = parseFloat(String(pRaw).replace(/\s/g, '').replace(',', '.'));
        if (!Number.isFinite(pu) || pu < 0) {
          errors.push(`Fila ${r}: ${COL.price} debe ser un número ≥ 0, o deje la celda vacía.`);
          continue;
        }
        unitPrice = Math.round(pu * 10000) / 10000;
      }
    }

    const qRounded = Math.round(qtyNum * 1000) / 1000;
    const total = Math.round(qRounded * unitPrice * 10000) / 10000;

    items.push({
      product_id: product.id,
      description: product.name || '',
      project_id: projectId,
      kardex_event: kardex,
      quantity: qRounded,
      unit_price: isExit ? 0 : unitPrice,
      total: isExit ? 0 : total,
      unit_of_measure: uom,
      condition: cond,
      _unitFormula: unitFormula,
      _unitIsCustom: unitIsCustom,
    });
  }

  if (items.length === 0 && errors.length === 0) {
    errors.push('No se importó ninguna fila con datos. Revise el archivo y la fila de encabezados.');
  }

  return { items, errors, warnings };
}
